import { useState } from 'react'

import {
  useLazyListPartiesQuery,
  useGetOneFatPartyQuery, // this one is the for initial load, which happens before the ui renders anything
  useUpsertPartyMutation,
  useBulkUpdatePartiesMutation,
  useUpdateSkillsetForPartiesMutation,
  useLazyListUsersQuery,
  useAssertUserForPartyMutation,
  useLazyListPartyTypesQuery,
  useBulkUpdateUsersMutation,
  useDeleteUserMutation,
  useDeletePartyMutation
} from '../../apiSlice/partyApi'

import { useBulkUpdateGroupsMutation} from '../../apiSlice/groupApi'

import { useBulkUpdateContractsMutation } from '../../apiSlice/contractApi'
import { useBulkUpdateAssetsMutation } from '../../apiSlice/assetApi'

import useSearchCallback from '../../apiSlice/useSearchCallback'
import useFetchAuxiliaryLists from '../../apiSlice/useFetchAuxiliaryLists'
import { BucketConfig } from '../../../sharedcomponents/5templates/detailCards/BucketConfig'
import {
  ArtificialDetailPropertyNames,
  RootObjectTypes
} from '../../../common/globalNotary'
import {
  getPushPull,
  getPartySkillsetAssociations
} from '../../apiSlice/pushPullHelper'
import { useLazyListJobTitlesQuery } from '../../apiSlice/jobTitleApi'
import { useNavigate } from 'react-router-dom'

export const createUserGridBulkUpdater = (bulkUpdateUsers, deleteUser) =>
  changes => ({
    unwrap: () => {
      const deletions = changes.filter(change =>
        Object.prototype.hasOwnProperty.call(change, 'deleted_at')
      )
      const updates = changes.filter(
        change => !Object.prototype.hasOwnProperty.call(change, 'deleted_at')
      )
      const operations = deletions.map(({ id }) =>
        deleteUser({ id }).unwrap()
      )

      if (updates.length) {
        operations.unshift(bulkUpdateUsers(updates).unwrap())
      }

      return Promise.all(operations)
    }
  })

const useParty = () => {
  const [loadParties] = useLazyListPartiesQuery()
  const [loadUsers] = useLazyListUsersQuery()
  const [loadPartyTypes] = useLazyListPartyTypesQuery()
  const [loadJobTitles] = useLazyListJobTitlesQuery()
  const [upsertPartyRecordCallback] = useUpsertPartyMutation()
  const [bulkUpdateGroups] = useBulkUpdateGroupsMutation()
  const [bulkAssignSkillsetToParties] = useUpdateSkillsetForPartiesMutation()
  const [saveUserForParty] = useAssertUserForPartyMutation()
  const [deleteUser] = useDeleteUserMutation()
  const [deleteParty] = useDeletePartyMutation()

  const [bulkUpdateParties] = useBulkUpdatePartiesMutation()
  const [bulkUpdateUsers] = useBulkUpdateUsersMutation()
  const bulkUpdateUsersFromGrid = createUserGridBulkUpdater(
    bulkUpdateUsers,
    deleteUser
  )
  const [bulkUpdateContracts] = useBulkUpdateContractsMutation()
  const [bulkUpdateAssets] = useBulkUpdateAssetsMutation()
  const navigate = useNavigate()

  const {
    searchGroupsCallback,
    searchNonContainerAssetsCallback,
    searchSkillsetsCallback,
    searchContractsCallback
  } = useSearchCallback()

  const [finalAuxiliaryRecords, setFinalAuxiliaryRecords] = useState(null)
  const [loading, setLoading] = useState(false)

  const constructAndSetFinalListBucket = (
    completeResponse,
    primaryUserRecord
  ) => {
    const finalListBuckets = {}
    finalListBuckets.primero = []

    finalListBuckets.segundo = [
      new BucketConfig(
        completeResponse[2].data,
        searchContractsCallback,
        'Accounts',
        null
      ),
      new BucketConfig(
        completeResponse[3].data,
        searchNonContainerAssetsCallback,
        'Locations',
        null
      ),
      new BucketConfig(
        completeResponse[4].data,
        searchSkillsetsCallback,
        'Owned Skillsets',
        null,
        'genericSelect',
        'decorated',
        {
          titleArray: ['Completed Date', 'Expired Date', 'Document Link'],
          controlArray: [
            {
              type: 'date',
              baseName: `${ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5}[INDEX].effective_at`,
              disabled: false
            },
            {
              type: 'date',
              baseName: `${ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5}[INDEX].expires_at`,
              disabled: false
            },
            {
              type: 'url',
              baseName: `${ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5}[INDEX].doc_url`,
              disabled: false
            }
          ]
        }
      )
    ]

    finalListBuckets.tercero = [
      new BucketConfig(
        completeResponse[0].data,
        searchGroupsCallback,
        'User Group',
        { type: 'user' }
      ),
      new BucketConfig(
        completeResponse[1].data,
        searchGroupsCallback,
        'Owner of Group',
        { noSystem: true }
      )
    ]

    finalListBuckets.partyTypes = completeResponse[5].data
    finalListBuckets.jobTitles = completeResponse[6].data

    setFinalAuxiliaryRecords(finalListBuckets)
  }

  const {
    constructValueAsPromiseCall,
    constructGetAllPartyTypesCall,
    constructGetAllJobTitlesCall
  } = useFetchAuxiliaryLists()
  const equalityEvaluator = (wideItem, narrowItem) => {
    return (
      wideItem.skillset_id === narrowItem.skillset_id &&
      wideItem.party_id === narrowItem.party_id
    )
  }

  const fetchAuxiliaryRecords = async primaryUserRecord => {
    const fetchCallArray = [
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1,
        primaryUserRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1]
      ),
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2,
        primaryUserRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2]
      ),
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3,
        primaryUserRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3]
      ),
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4,
        primaryUserRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4]
      ),
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5,
        primaryUserRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5],
        ['party_id', 'skillset_id', 'effective_at', 'expires_at', 'doc_url'],
        equalityEvaluator
      ),

      constructGetAllPartyTypesCall(),
      constructGetAllJobTitlesCall()
    ]

    /// this Promise.all will not reject, errors come back on the error object,
    /// if everything works out, the error object is null or undefined
    Promise.all(fetchCallArray.map(f => f.call())).then(res => {
      constructAndSetFinalListBucket(res, primaryUserRecord)
    })
  }

  const typeIdToDisplayName = typeId => {
    switch (typeId) {
      case 'user':
        return 'User'
      case 'contact':
        return 'People'
      default:
        return ''
    }
  }

  const listPartyTypes = async () => {
    const {
      data: { data: types } = { data: [] }
    } = await loadPartyTypes()

    return types
  }

  const listJobTitles = async () => {
    const {
      data: { data: jobTitles } = { data: [] }
    } = await loadJobTitles()

    return jobTitles
  }

  const makeInMemoryPartyRecord = type => {
    const addresses =
      type === 'user'
        ? []
        : [
            {
              country: '',
              region: '',
              city: '',
              street: '',
              unit: '',
              name: 'generic',
              postal: ''
            }
          ]

    const returnValue = {
      base_type: type,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      avatar_media_id: null,
      job_title_id: '',
      avatar_url: '',
      profile: [],
      settings: [],
      active: true,
      assertUser: type === 'user',
      assertGuest: false,
      addresses,
      _objectId: '',
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3_INITIAL_VALUES]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5_INITIAL_VALUES]: []
    }

    return returnValue
  }

  const removeParty = async id => {
    deleteParty({ id }).then(() => {
      navigate(-1)
    }).catch(err => {
      console.error(err)
      alert('Could not delete party. Please check that other objects do not depend on it.')
    })
  }

  const removeUser = async id => {
    deleteUser({ id }).then(() => {
      navigate(-1)
    }).catch(err => {
      console.error(err)
      alert('Could not delete user. Please check that other objects do not depend on it.')
    })
  }

  const upsertPartyCallback = primaryRecord => {
    const { suspended_at: suspendedAt } = primaryRecord
    setLoading(true)

    return new Promise((resolve, reject) => {
      upsertPartyRecordCallback(primaryRecord)
        .unwrap()
        .then(primaryResult => {
          const id = primaryResult.data.id
            ? primaryResult.data.id
            : primaryResult.data.data.id

          const auxiliaryUpdateCallArray = []

          // Facility saving of the user record if not just
          // a contact
          // NOTE, we really should be using the values of the
          // slider and then setting that instead.
          // NOTE x2, we are currently fixing the identities
          // array. Again, this is just the state of the UI. We
          // will address in future.
          if (primaryRecord.type === 'user') {
            auxiliaryUpdateCallArray.push(
              saveUserForParty({
                id,
                ...(primaryRecord.user ?? {}),
                username: primaryRecord.user?.username || primaryRecord.email || null,
                identities: [{ type: 'email', value: primaryRecord.email.trim() }],
                // Note, currently the crud admin governs sso/otp (guest
                // login) in an either/or manner.
                allow_sso: !primaryRecord.assertGuest,
                allow_otp: primaryRecord.assertGuest,
                system_admin: primaryRecord.user?.system_admin || false,
                suspended_at: suspendedAt ?? null,
              })
            )
          } else if (primaryRecord.user) {
            // We have a user record and they are not set as a user.
            // Therefore, we need to delete their user account.
            auxiliaryUpdateCallArray.push(
              deleteUser({ id })
            )
          }

          const groupMembershipPushPull = getPushPull(
            primaryRecord,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES
          )

          const ownerPushPull = getPushPull(
            primaryRecord,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES
          )

          const contractPushPull = getPushPull(
            primaryRecord,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3_INITIAL_VALUES
          )

          const assetPushPull = getPushPull(
            primaryRecord,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES
          )

          const currentSkillsetList =
            primaryRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5]

          for (let i = 0; i < currentSkillsetList.length; i++) {
            currentSkillsetList[i].party_id = id
          }

          const partyAndSkillset = getPartySkillsetAssociations(
            primaryRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5],
            primaryRecord[
              ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5_INITIAL_VALUES
            ]
          )

          if (ownerPushPull.push.length || ownerPushPull.pull.length) {
            auxiliaryUpdateCallArray.push(
              bulkUpdateGroups([
                ...ownerPushPull?.push?.map(groupId => ({
                  id: groupId,
                  owner_id_assign: id
                })),
                ...ownerPushPull?.pull?.map(groupId => ({
                  id: groupId,
                  owner_id_unassign: id
                }))
              ])
            )
          }

          if (
            groupMembershipPushPull.push.length ||
            groupMembershipPushPull.pull.length
          ) {
            auxiliaryUpdateCallArray.push(
              bulkUpdateGroups([
                ...groupMembershipPushPull?.push?.map(groupId => ({
                  id: groupId,
                  item_id_assign: id
                })),
                ...groupMembershipPushPull?.pull?.map(groupId => ({
                  id: groupId,
                  item_id_unassign: id
                }))
              ])
            )
          }

          if (contractPushPull.push.length || contractPushPull.pull.length) {
            auxiliaryUpdateCallArray.push(
              bulkUpdateContracts([
                ...contractPushPull?.push?.map(contractId => ({
                  id: contractId,
                  party_id_assign: id
                })),
                ...contractPushPull?.pull?.map(contractId => ({
                  id: contractId,
                  party_id_unassign: id
                }))
              ])
            )
          }

          if (assetPushPull.push.length || assetPushPull.pull.length) {
            auxiliaryUpdateCallArray.push(
              bulkUpdateAssets([
                ...assetPushPull?.push?.map(assetId => ({
                  id: assetId,
                  party_id_assign: id
                })),
                ...assetPushPull?.pull?.map(assetId => ({
                  id: assetId,
                  party_id_unassign: id
                }))
              ])
            )
          }

          auxiliaryUpdateCallArray.push(
            bulkAssignSkillsetToParties(partyAndSkillset.map(ps => ({
              party_id: id,
              skillset_id: ps.skillset_id,
              effective_at: ps.effective_at ? new Date(ps.effective_at) : null,
              expires_at: ps.expires_at ? new Date(ps.expires_at) : null,
              doc_url: ps.doc_url || null,
              deleted_at: ps.deleted_at ? new Date(ps.deleted_at) : null,
            })))
          )

          if (auxiliaryUpdateCallArray.length) {
            Promise.all(auxiliaryUpdateCallArray)
              .then(results => {
                const errors = results?.flatMap(r => r.data?.errors ?? [])
                errors.length === 0
                  ? resolve({ id, type: RootObjectTypes.PARTY })
                  : reject(
                    'The following errors have occurred: ' +
                    errors.map(e => e.msg).join(", ")
                  )
              })
              .catch(reject)
          } else {
            resolve(id)
          }
        })
        .catch(reject)
        .finally(() => {
          setLoading(false)
        })
    })
  }

  return {
    loadParties,
    loadUsers,
    listPartyTypes,
    listJobTitles,
    typeIdToDisplayName,
    bulkUpdateParties,
    bulkUpdateUsers,
    bulkUpdateUsersFromGrid,
    useGetOneFatPartyQuery,
    fetchAuxiliaryRecords,
    makeInMemoryPartyRecord,
    finalAuxiliaryRecords,
    upsertPartyCallback,
    removeParty,
    removeUser,
    loading
  }
}

export default useParty
